import { getAuthToken } from '../services/salesforceService';

/**
 * Checks if the org has valid auth tokens, attempting to refresh if needed.
 * 
 * @param orgId - The org ID to check auth for
 * @returns Promise that resolves with auth tokens or rejects with auth error
 * @throws Error with requiresReauth property if tokens are expired and can't be refreshed
 */
export async function checkOrgAuth(orgId: string) {
  try {
    // This will attempt to get tokens and refresh if needed
    const tokens = await getAuthToken(orgId);
    return tokens;
  } catch (error: any) {
    console.error('Auth token check failed:', error);
    
    // Re-throw auth errors so caller can handle with ReauthModal
    if (error.message === 'INVALID_SESSION' || error.requiresReauth || error.needsReconnect) {
      throw error;
    }
    
    // For other errors, throw as-is
    throw error;
  }
}

/**
 * Higher-order function that wraps an async operation with auth checking.
 * If auth fails, it will call the onAuthError callback instead of proceeding.
 * 
 * @param orgId - The org ID to check auth for
 * @param operation - The async operation to perform if auth succeeds
 * @param onAuthError - Callback to handle auth errors (typically shows ReauthModal)
 */
export async function withAuthCheck<T>(
  orgId: string,
  operation: () => Promise<T>,
  onAuthError: (error: any) => void
): Promise<T | void> {
  try {
    // First check auth
    await checkOrgAuth(orgId);
    
    // If auth succeeds, perform the operation
    return await operation();
  } catch (error: any) {
    // Handle auth errors
    if (error.message === 'INVALID_SESSION' || error.requiresReauth || error.needsReconnect) {
      onAuthError(error);
      return;
    }
    
    // Re-throw non-auth errors
    throw error;
  }
}